import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import StatsRibbon from "@/components/StatsRibbon";
import { Section, SectionLabel, SectionTitle, FadeIn } from "@/components/Section";
import { Link } from "react-router-dom";
import { ArrowRight, Ship, Shield, ClipboardList, Anchor, Crosshair, Heart, Truck, AlertTriangle } from "lucide-react";
import heroVessel from "@/assets/hero-vessel.png";
import vesselAction1 from "@/assets/vessel-action-1.jpg";

const capabilities = [
  { icon: Ship, num: "01", title: "Crew Transfer Operations", desc: "Safe, scheduled offshore personnel transportation to FPSOs, drillships, and fixed installations worldwide.", href: "/services" },
  { icon: Anchor, num: "02", title: "PSV Operations", desc: "Platform Supply Vessel management — deck cargo, bulk liquids, and consumables delivery to offshore installations with DP-2 station-keeping.", href: "/services" },
  { icon: ClipboardList, num: "03", title: "Vessel Operations", desc: "Complete technical and operational management of the AIRCAT 35 Crew Transfer Vessel and PSV fleet.", href: "/services" },
  { icon: Shield, num: "04", title: "Safety & ISM", desc: "Full Safety Management System with ISM, MLC, ISPS & SOLAS / HSC Code certification.", href: "/safety" },
];

const missionProfiles = [
  { icon: Ship, title: "Personnel Transfer", desc: "Scheduled crew rotations to offshore production facilities" },
  { icon: Truck, title: "Light Cargo Transport", desc: "Critical spares and time-sensitive supplies via Combi variant" },
  { icon: AlertTriangle, title: "Medevac Readiness", desc: "Rapid medical evacuation capability with onboard first-aid station" },
  { icon: Crosshair, title: "Offshore Standby", desc: "Station-keeping and standby support during offshore operations" },
];

const Index = () => (
  <Layout>
    <PageHero
      tag="Offshore Crew Transfer Specialists"
      title={<>Offshore personnel transportation.<br /><span className="accent-text">Precision delivered.</span></>}
      subtitle="Safe, reliable Crew Transfer Vessel and PSV operations supporting offshore production and supply chain needs across West Africa, the Caribbean, and South America."
      buttons={[
        { label: "Get in Touch", href: "/contact", variant: "primary" },
        { label: "Our Solutions", href: "/services", variant: "outline" },
      ]}
      backgroundImage={heroVessel}
      backgroundVideo="/videos/hero-vessel.mp4"
      fullHeight
    />

    <StatsRibbon stats={[
      { value: "AIRCAT 35", label: "Crew Transfer Vessel" },
      { value: "RMI", label: "Flag State" },
      { value: "ABS", label: "Classification" },
      { value: "DPS-2", label: "DP Class" },
    ]} />

    {/* Philosophy statement */}
    <Section offWhite>
      <div className="max-w-4xl mx-auto text-center">
        <FadeIn>
          <p className="text-teal/60 text-[10px] font-body tracking-[4px] uppercase mb-6">Our Philosophy</p>
          <blockquote className="font-display text-[clamp(1.5rem,3vw,2.5rem)] text-navy leading-[1.2] tracking-tight mb-6">
            "Velocity Offshore was founded with a simple objective: to deliver the fastest, safest, and most cost-efficient offshore personnel transportation solutions."
          </blockquote>
          <p className="text-text-light font-body text-lg font-light">
            Designed by operators. Built for offshore reality.
          </p>
        </FadeIn>
      </div>
    </Section>

    {/* Vessel showcase */}
    <Section>
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <SectionLabel>The Vessel</SectionLabel>
          <SectionTitle>AIRCAT <span className="accent-text">35 Crewliner</span></SectionTitle>
          <FadeIn delay={0.1}>
            <p className="text-text-light font-body text-lg leading-relaxed mb-8 font-light">
              Proven high-speed offshore transport platform delivering superior speed, seakeeping, and fuel efficiency. Purpose-built for crew transfer in tropical and open ocean conditions.
            </p>
            <div className="grid grid-cols-4 gap-4 mb-10">
              {[
                { value: "35m", label: "Length" },
                { value: "80", label: "PAX Capacity" },
                { value: "46+", label: "Knots" },
                { value: "600+", label: "NM Range" },
              ].map((stat) => (
                <div key={stat.label} className="relative p-5 rounded-xl bg-off-white border border-border group hover:border-teal/30 transition-colors">
                  <div className="font-display text-2xl tracking-tight">
                    <span className="accent-text">{stat.value}</span>
                  </div>
                  <div className="text-text-muted font-body text-[10px] tracking-[2px] uppercase mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
            <Link to="/fleet" className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-navy text-primary-foreground text-[12px] font-bold tracking-[1.5px] uppercase rounded-full hover:bg-teal transition-all duration-300 font-body">
              Explore Fleet
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
        <FadeIn delay={0.2}>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-navy/20 glow-teal">
              <img src={vesselAction1} alt="AIRCAT 35 Crew Transfer Vessel — Velocity Offshore at sea" className="w-full h-auto" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-2xl bg-gradient-to-br from-teal/20 to-lime/10 blur-2xl" />
            <div className="absolute -top-4 -left-4 w-20 h-20 rounded-full bg-gradient-to-br from-lime/15 to-transparent blur-xl" />
          </div>
        </FadeIn>
      </div>
    </Section>

    {/* Typical Mission Profile */}
    <Section offWhite>
      <SectionLabel>Typical Mission Profile</SectionLabel>
      <SectionTitle>Operationally proven <span className="accent-text">capabilities</span></SectionTitle>
      <FadeIn>
        <p className="text-text-light font-body text-lg max-w-[600px] mb-12 font-light leading-relaxed">
          Field-tested across demanding offshore environments, our vessels and crew are ready for the full spectrum of offshore personnel transportation and support operations.
        </p>
      </FadeIn>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {missionProfiles.map((m, i) => (
          <FadeIn key={m.title} delay={i * 0.1}>
            <div className="bg-card rounded-2xl p-8 border border-border hover-lift group">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal/10 to-lime/10 flex items-center justify-center mb-6 group-hover:from-teal/20 group-hover:to-lime/20 transition-colors">
                <m.icon className="w-6 h-6 text-teal" />
              </div>
              <h3 className="font-display text-lg text-navy tracking-tight mb-3">{m.title}</h3>
              <p className="text-text-light font-body text-sm leading-relaxed">{m.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>

    {/* Capabilities */}
    <Section dark>
      <SectionLabel>What We Do</SectionLabel>
      <SectionTitle light>Integrated offshore <span className="accent-text">solutions</span></SectionTitle>
      <FadeIn>
        <p className="text-primary-foreground/40 font-body text-lg max-w-[600px] mb-16 font-light leading-relaxed">
          Fully-managed Crew Transfer Vessel and PSV operations with transparent accountability — from crew marshalling and supply logistics to FPSO personnel and cargo transfer.
        </p>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-6">
        {capabilities.map((cap, i) => (
          <FadeIn key={cap.title} delay={i * 0.1}>
            <Link
              to={cap.href}
              className="group relative flex gap-6 p-8 rounded-2xl glass-card hover:bg-primary-foreground/8 transition-all duration-500 hover:-translate-y-1"
            >
              <div className="flex-shrink-0">
                <span className="text-teal/20 font-display text-5xl font-bold">{cap.num}</span>
              </div>
              <div>
                <h3 className="font-display text-xl text-primary-foreground tracking-tight mb-3">{cap.title}</h3>
                <p className="text-primary-foreground/40 text-sm font-body leading-relaxed">{cap.desc}</p>
                <div className="mt-4 flex items-center gap-2 text-teal text-[11px] font-bold tracking-[1.5px] uppercase font-body opacity-0 group-hover:opacity-100 transition-all duration-300">
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </Section>

    {/* Safety & credibility */}
    <Section>
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <FadeIn>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "ABS", label: "Classification Society" },
                { value: "DPS-2", label: "DP Certified" },
                { value: "SOLAS", label: "HSC Code Compliant" },
                { value: "ISM", label: "DOC / SMC Certified" },
              ].map((item) => (
                <div key={item.label} className="p-7 rounded-2xl bg-off-white border border-border text-center hover-lift">
                  <div className="font-display text-3xl tracking-tight mb-2">
                    <span className="accent-text">{item.value}</span>
                  </div>
                  <div className="text-text-muted font-body text-[10px] tracking-[2px] uppercase">{item.label}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
        <div className="order-1 lg:order-2">
          <SectionLabel>About Velocity</SectionLabel>
          <SectionTitle>Operator-grade rigour.<br /><span className="accent-text">Maritime precision.</span></SectionTitle>
          <FadeIn delay={0.1}>
            <p className="text-text-light font-body text-lg leading-relaxed mb-4 font-light">
              Safety, reliability, and operational uptime are the foundation of Velocity Offshore's operating philosophy. Built on decades of operational experience in high-speed offshore crew transportation.
            </p>
            <p className="text-text-light font-body text-lg leading-relaxed mb-8 font-light">
              Velocity Offshore Holdings LLC is a Marshall Islands-incorporated vessel operating company providing dedicated Crew Transfer Vessel services to offshore production facilities globally. Class compliant, flag compliant, and SOLAS / HSC Code certified.
            </p>
            <Link to="/about" className="group inline-flex items-center gap-2.5 text-teal font-body text-[13px] font-bold tracking-wide hover:gap-4 transition-all">
              Learn About Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeIn>
        </div>
      </div>
    </Section>
  </Layout>
);

export default Index;
